// Pluralization helper. Saves repeating `${n === 1 ? "" : "s"}` at
// every call site, and -- more importantly -- gives us a single
// place to swap in a real Intl.PluralRules lookup if we ever need
// to localise the public page beyond English.
function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"}`
}

type FormatAgoOpts = {
  // Render "X seconds ago" when the diff is below a minute. The
  // footer's poll counter wants this; the KPI sub-text doesn't.
  showSeconds?: boolean
  // Anything strictly below this returns "just now". The footer
  // tightens it to 5 s so it doesn't flicker between "just now" and
  // "1 second ago" on every tick. KPI sub-text leaves it at the
  // default 60 s.
  justNowBelowSec?: number
  // Render "X days ago" once the diff exceeds 24 h. KPIs need this
  // because `lastIncidentAt` can be up to 30 days back; the footer
  // doesn't (anything that old means the page is stuck on a stale
  // payload, surfaced separately as the error banner).
  showDays?: boolean
}

// Shared "X units ago" core. Both `formatUpdatedAgo` (footer poll
// indicator) and `formatRelativeTime` (KPI sub-text) wrap this with
// their own null-handling and option set so the tier ladder stays
// in one place.
function formatAgoCore(diffSec: number, opts: FormatAgoOpts): string {
  if (diffSec < (opts.justNowBelowSec ?? 60)) return "just now"
  if (opts.showSeconds && diffSec < 60)
    return `${plural(diffSec, "second")} ago`
  const mins = Math.floor(diffSec / 60)
  if (mins < 60) return `${plural(mins, "minute")} ago`
  const hours = Math.floor(mins / 60)
  if (!opts.showDays || hours < 24) return `${plural(hours, "hour")} ago`
  const days = Math.floor(hours / 24)
  return `${plural(days, "day")} ago`
}

// "Updated 12 seconds ago" copy for the footer poll pip. Public-
// facing so the wording avoids abbreviations that aren't broadly
// understood ("just now" instead of "now", "minutes" instead of
// "min").
export function formatUpdatedAgo(ts: Date | null, now: Date): string {
  if (!ts) return "—"
  const diffSec = Math.max(0, Math.round((now.getTime() - ts.getTime()) / 1000))
  return formatAgoCore(diffSec, { showSeconds: true, justNowBelowSec: 5 })
}

// Relative-time formatter for KPI sub-text ("12 minutes ago", "3
// days ago"). Returns empty string for nullish input so the tile
// sub renders blank rather than a misleading "—".
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return ""
  const ts = new Date(iso)
  if (Number.isNaN(ts.getTime())) return ""
  const diffSec = Math.max(0, Math.round((Date.now() - ts.getTime()) / 1000))
  return formatAgoCore(diffSec, { showDays: true })
}

// Turn a duration in minutes into a customer-readable phrase. We
// stop at hours; a multi-day outage is unusual enough that
// rendering it in hours ("36 h 12 min") still reads better than
// inventing a "days" tier on the public page.
export function formatDurationMinutes(minutes: number): string {
  if (minutes <= 0) return "less than a minute"
  if (minutes < 60) return plural(minutes, "minute")
  const hours = Math.floor(minutes / 60)
  const rem = minutes % 60
  if (rem === 0) return plural(hours, "hour")
  return `${hours} h ${rem} min`
}

// Module-level so the arrays aren't reallocated on every call --
// the page re-renders on every poll and `formatIncidentDate` runs
// once per incident header in the timeline.
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

// Date-only formatter for incident headers ("Tuesday, Apr 27").
// Human-friendly day-of-week so customers can quickly anchor the
// outage in their own week without parsing the ISO timestamp.
// Rendered in UTC so customers in different timezones see the same
// incident date for the same incident -- otherwise an outage that
// straddles midnight UTC would show up under different headers in
// Oslo vs Anchorage.
export function formatIncidentDate(iso: string): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
}

// Latency formatter used in service rows ("312 ms", "1.2 s").
// Mirrors the backend's formatLatencyMs so summary text and on-row
// latency badges read identically.
export function formatLatencyMs(ms: number | null | undefined): string {
  if (ms == null || ms <= 0) return "—"
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

// Rounded-to-2-decimals percent for the per-category 30-day uptime
// readout. Returns "—" when the input is nullish so the page
// distinguishes "no data yet" from "100% up".
export function formatUptimePercent(pct: number | null | undefined): string {
  if (pct == null) return "—"
  if (pct >= 100) return "100%"
  if (pct >= 99.99) return `${pct.toFixed(3)}%`
  return `${pct.toFixed(2)}%`
}
